import { ActiveGameStore as BaseStore } from '@bn-games/history-core';
import { CELL_COUNT, type NonEmptyDigit } from '../game/types.js';
import type { GameStateData, SolveEvent } from '../game/GameState.js';
import type { ActiveSession, SerializableSolveEvent } from './types.js';
import { ACTIVE_GAME_STORAGE_KEY, HISTORY_SCHEMA_VERSION } from './types.js';

// Per-app singleton wrapping @bn-games/history-core's generic store with
// sudoku's storage key + schema version + shape predicate.
export const ActiveGameStore = new BaseStore<ActiveSession>({
  storageKey: ACTIVE_GAME_STORAGE_KEY,
  schemaVersion: HISTORY_SCHEMA_VERSION,
  isValid: (parsed) =>
    parsed.puzzle !== undefined &&
    Array.isArray(parsed.user) &&
    Array.isArray(parsed.pencil) &&
    Array.isArray(parsed.history) &&
    Array.isArray(parsed.redoStack),
});

function toSerializableEvent(event: SolveEvent): SerializableSolveEvent {
  if (event.kind === 'enter') {
    return {
      kind: 'enter',
      cellIndex: event.cellIndex,
      prevDigit: event.prevDigit,
      newDigit: event.newDigit,
      clearedPencils: [...event.clearedPencils],
      clearedPeerPencils: [...event.clearedPeerPencils],
      wasError: event.wasError,
    };
  }
  if (event.kind === 'erase') {
    return {
      kind: 'erase',
      cellIndex: event.cellIndex,
      prevDigit: event.prevDigit,
      prevPencils: [...event.prevPencils],
    };
  }
  return {
    kind: 'pencil',
    cellIndex: event.cellIndex,
    digit: event.digit,
    added: event.added,
  };
}

function fromSerializableEvent(event: SerializableSolveEvent): SolveEvent {
  if (event.kind === 'enter') {
    return {
      kind: 'enter',
      cellIndex: event.cellIndex,
      prevDigit: event.prevDigit,
      newDigit: event.newDigit,
      clearedPencils: new Set<NonEmptyDigit>(event.clearedPencils),
      clearedPeerPencils: event.clearedPeerPencils ?? [],
      wasError: event.wasError,
    };
  }
  if (event.kind === 'erase') {
    return {
      kind: 'erase',
      cellIndex: event.cellIndex,
      prevDigit: event.prevDigit,
      prevPencils: new Set<NonEmptyDigit>(event.prevPencils),
    };
  }
  return {
    kind: 'pencil',
    cellIndex: event.cellIndex,
    digit: event.digit,
    added: event.added,
  };
}

/** Snapshot the engine state into the on-disk shape. */
export function toActiveSession(
  state: GameStateData,
  id: string,
  now: number = Date.now(),
): ActiveSession {
  return {
    id,
    version: HISTORY_SCHEMA_VERSION,
    puzzle: state.puzzle,
    user: state.user.slice(),
    pencil: state.pencil.map((s) => [...s]),
    history: state.history.map(toSerializableEvent),
    redoStack: state.redoStack.map(toSerializableEvent),
    startedAt: state.startedAt,
    hintsUsed: state.hintsUsed,
    errorsMade: state.errorsMade,
    updatedAt: now,
  };
}

/** Rehydrate a saved session into a live engine state. */
export function fromActiveSession(session: ActiveSession): GameStateData {
  const pencil: Array<Set<NonEmptyDigit>> = new Array(CELL_COUNT);
  for (let i = 0; i < CELL_COUNT; i++) {
    pencil[i] = new Set<NonEmptyDigit>(session.pencil[i] ?? []);
  }
  return {
    puzzle: session.puzzle,
    user: session.user.slice(),
    pencil,
    history: session.history.map(fromSerializableEvent),
    redoStack: session.redoStack.map(fromSerializableEvent),
    startedAt: session.startedAt,
    hintsUsed: session.hintsUsed,
    errorsMade: session.errorsMade,
  };
}
